import type {FileEdit} from '@microsoft/agent-host-protocol'

import {TOOL_NAMES} from '../../constants/tool-names'
import {parseApplyPatchPaths, type ParsedApplyPatchWrite} from './apply-patch-parser'
import type {ArtifactOperation} from './artifact-types'

/**
 * One settled tool call, normalised away from its transport.
 *
 * `resolveArgs` is a callback rather than a plain object because AHP sends a
 * single-argument tool's input as a bare scalar rather than an object, and only
 * the caller knows how to widen that. `primaryKey` names the argument such a
 * scalar should become.
 */
export interface ToolWriteRecord {
  toolCallId: string
  toolName: string
  resolveArgs: (primaryKey: string) => Record<string, unknown>
  succeeded: boolean
  preview?: string
  fileEdits?: readonly FileEdit[]
}

export type Write = ParsedApplyPatchWrite

/** These `str_replace_editor` subcommands mutate a file; `view` does not, while `undo_edit` invalidates reconstructed content. */
const STR_REPLACE_EDITOR_WRITE_COMMANDS: Record<string, ArtifactOperation> = {
  create: 'created',
  str_replace: 'modified',
  insert: 'modified',
  undo_edit: 'modified',
}

/**
 * Tools that write a single file identified by an explicit `path` argument,
 * mapped to the operation they perform.
 *
 * `apply_patch` is absent because it carries no `path` — its targets are
 * embedded in the patch body and are extracted separately.
 */
const SINGLE_PATH_WRITE_TOOLS: Record<string, ArtifactOperation> = {
  [TOOL_NAMES.CREATE]: 'created',
  [TOOL_NAMES.EDIT]: 'modified',
  [TOOL_NAMES.STR_REPLACE]: 'modified',
  [TOOL_NAMES.INSERT]: 'modified',
  [TOOL_NAMES.UNDO_EDIT]: 'modified',
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function wholeFileContent(args: Record<string, unknown>): string | undefined {
  const body = args['file_text'] ?? args['content']
  return typeof body === 'string' ? body : undefined
}

export function writesFor(record: ToolWriteRecord): Write[] {
  const {toolName} = record

  const singlePathOperation = SINGLE_PATH_WRITE_TOOLS[toolName]
  if (singlePathOperation) {
    const args = record.resolveArgs('path')
    const path = args['path']
    if (typeof path !== 'string' || !path) return []
    const content = singlePathOperation === 'created' ? wholeFileContent(args) : undefined
    const oldContent = args['old_str']
    const newContent = args['new_str']
    return [
      {
        path,
        operation: singlePathOperation,
        ...(content === undefined ? {} : {content}),
        ...(typeof oldContent === 'string' && typeof newContent === 'string'
          ? {replacements: [{find: oldContent, replace: newContent}]}
          : {}),
      },
    ]
  }

  if (toolName === TOOL_NAMES.STR_REPLACE_EDITOR) {
    const args = record.resolveArgs('path')
    const command = args['command']
    const operation = typeof command === 'string' ? STR_REPLACE_EDITOR_WRITE_COMMANDS[command] : undefined
    if (!operation) return []
    const path = args['path']
    if (typeof path !== 'string' || !path) return []
    const content = command === 'create' ? wholeFileContent(args) : undefined
    const oldContent = args['old_str']
    const newContent = args['new_str']
    return [
      {
        path,
        operation,
        ...(content === undefined ? {} : {content}),
        ...(typeof oldContent === 'string' && typeof newContent === 'string'
          ? {replacements: [{find: oldContent, replace: newContent}]}
          : {}),
      },
    ]
  }

  if (toolName === TOOL_NAMES.APPLY_PATCH) {
    const content = record.resolveArgs('content')['content']
    return typeof content === 'string' ? parseApplyPatchPaths(content) : []
  }

  return []
}
